import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per snooker game
 * {
 *   gameType: "15-red" | "6-red",
 *   gameMode: "singles",
 *   attempts: [{ shotBy: "user" | "opponent", ballColor, points }],
 *   userScore: number,
 *   opponentScore: number,
 *   winner: "user" | "opponent" | null,
 *   playerA: string | null, // id or custom_x
 *   playerB: string | null,
 *   playerAName?: string,
 *   playerBName?: string
 * }
 */

// This map automatically assigns points based on ball color.
const ballToPointsMap = {
  miss: 0,
  red: 1,
  yellow: 2,
  green: 3,
  brown: 4,
  blue: 5,
  pink: 6,
  black: 7,
};

// Color map for ball colors
const ballColorMap = {
  miss: "#888888",
  red: "#e53e3e",
  yellow: "#ecc94b",
  green: "#48bb78",
  brown: "#b7791f",
  blue: "#3182ce",
  pink: "#d53f8c",
  black: "#2d3748",
};

const brandBlue = "#3498db";
const brandBlueDark = "#2980b9";

export const AddSnooker = () => {
  const location = useLocation();
  const data = location.state || {};

  // This state holds multiple Snooker games within the same arena usage
  const [snookerGames, setSnookerGames] = useState([]);
  const navigate = useNavigate();
  const [availableUsers, setAvailableUsers] = useState([]);
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  // Tailwind class definitions
  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold py-3 px-5 rounded-lg shadow-md transition-all duration-200 mb-5",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    noGamesMessage:
      "text-center p-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 text-gray-500 text-lg",
  };

  const handleSaveAll = async () => {
    // Validate all games before saving
    const validationErrors = validateAllGames();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following issues before saving:\n\n" +
          validationErrors.join("\n")
      );
      return;
    }

    try {
      // Convert "0" (Guest Arena) to null so MySQL can store it correctly
      const arenaBookingId =
        data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

      const bodyData = {
        arenaBookingId,
        sport: data.sport,
        games: snookerGames,
      };

      // Add either userId or instituteId based on context
      if (isInstitute) {
        bodyData.instituteId = instituteId;
      } else {
        bodyData.userId = user?.uid;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to save match");
      }

      const responseData = await response.json();
      toast.success("Saved all snooker games!");

      // Navigate based on context
      if (isInstitute) {
        navigate("/institute-owner");
      } else {
        navigate("/Manage");
      }
    } catch (error) {
      console.error("Error saving match:", error);
      toast.error("Error saving match");
    }
  };

  // Validation function for all games
  const validateAllGames = () => {
    const errors = [];

    snookerGames.forEach((game, index) => {
      const gameErrors = validateSnookerGame(game, index + 1);
      errors.push(...gameErrors);
    });

    return errors;
  };

  // Validate individual snooker game
  const validateSnookerGame = (game, gameNumber) => {
    const errors = [];

    // Check if players are selected
    if (!game.playerA || !game.playerB) {
      errors.push(`Game ${gameNumber}: Both players must be selected`);
    }

    // Check if same player is selected for both positions
    if (game.playerA && game.playerB && game.playerA === game.playerB) {
      errors.push(
        `Game ${gameNumber}: Cannot select the same player for both positions`
      );
    }

    // Check if game has any attempts
    if (!game.attempts || game.attempts.length === 0) {
      errors.push(
        `Game ${gameNumber}: Game must have at least one shot attempt`
      );
    }

    // Check if game has a clear winner (not a draw with 0-0)
    if (game.userScore === 0 && game.opponentScore === 0) {
      errors.push(`Game ${gameNumber}: Game must have scoring attempts`);
    }

    return errors;
  };

  // Add a new empty Snooker game
  const handleAddSnookerGame = () => {
    const newGame = {
      gameType: "15-red", // default
      gameMode: "singles", // Snooker is always singles (1v1)
      attempts: [],
      userScore: 0,
      opponentScore: 0,
      winner: null,
      playerA: null, // User can select any player
      playerB: null, // User can select any player
    };
    setSnookerGames((prev) => [...prev, newGame]);
  };

  // Remove an entire snooker game
  const handleRemoveGame = (index) => {
    setSnookerGames((prev) => prev.filter((_, i) => i !== index));
  };

  // When a child form updates a specific game, update our main state
  const handleUpdateGame = (index, updatedGame) => {
    setSnookerGames((prevGames) =>
      prevGames.map((g, i) => (i === index ? updatedGame : g))
    );
  };

  return (
    <>
      <HeaderMain pageType="AddGame" />
      <div className={classes.container}>
        <div className={classes.header} style={{ borderColor: brandBlue }}>
          <h2 className={classes.headerTitle}>
            {data.sport} Match at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time && (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            )}
          </p>
        </div>

        <button className={classes.addButton} onClick={handleAddSnookerGame}>
          Add New Snooker Game
        </button>

        {snookerGames.length === 0 && (
          <div className={classes.noGamesMessage}>
            No games added yet. Click the button above to add your first snooker
            game.
          </div>
        )}

        {snookerGames.map((game, index) => (
          <SnookerGameForm
            key={index}
            game={game}
            gameNumber={index + 1}
            availableUsers={availableUsers}
            onUpdate={(updatedGame) => handleUpdateGame(index, updatedGame)}
            onRemove={() => handleRemoveGame(index)}
          />
        ))}

        {snookerGames.length > 0 && (
          <button onClick={handleSaveAll} className={classes.saveButton}>
            Save All Games
          </button>
        )}
      </div>
    </>
  );
};

// A child component for editing one Snooker game
const SnookerGameForm = ({
  game,
  gameNumber,
  availableUsers,
  onUpdate,
  onRemove,
}) => {
  // We can keep local state if desired, or update the parent directly.
  const [localGame, setLocalGame] = useState(game);

  // Tailwind class definitions
  const formClasses = {
    gameCard: "border border-gray-200 rounded-xl p-6 mb-6 bg-gray-50 shadow-sm",
    gameHeader:
      "flex justify-between items-center border-b border-gray-200 pb-4 mb-5",
    gameTitle: "text-xl font-semibold text-gray-800 m-0",
    removeButton:
      "bg-red-400 hover:bg-red-500 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200",
    formGroup: "mb-5",
    label: "block mb-2 font-medium text-gray-700",
    select:
      "w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    addButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-4 py-2 rounded-lg transition-all duration-200",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
    playerSelectionRow: "grid grid-cols-1 md:grid-cols-2 gap-5 mb-5",
    tableWrapper: "w-full overflow-x-auto",
    table:
      "w-full text-sm border-collapse mt-5 rounded-lg overflow-hidden shadow-sm",
    tableHeader:
      "bg-gray-100 text-gray-800 font-semibold p-3 text-left border-b border-gray-200",
    tableCell: "p-3 border-b border-gray-200 bg-white",
    scoreCard:
      "grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-5 rounded-lg mt-5 shadow-sm",
    scoreBox: "text-center p-4 rounded-lg bg-gray-50",
    scoreLabel: "text-sm text-gray-500 mb-1",
    scoreValue: "text-3xl font-bold text-gray-800",
    winnerBox: "text-center p-4 rounded-lg bg-blue-50",
    winnerLabel: "text-sm text-blue-700 mb-1",
    winnerValue: "text-xl font-bold text-blue-900",
    removeAttemptButton:
      "bg-red-400 hover:bg-red-500 text-white px-2 py-1 rounded text-xs transition-colors",
    ballColorCell: "flex items-center gap-2",
    colorIndicator: "w-4 h-4 rounded-full inline-block",
  };

  const updateParent = (updated) => {
    setLocalGame(updated);
    onUpdate(updated);
  };

  // Change game type
  const handleGameTypeChange = (e) => {
    const updated = { ...localGame, gameType: e.target.value };
    recalcScoresAndUpdate(updated);
  };

  // Add a new attempt
  const handleAddAttempt = () => {
    const newAttempt = {
      shotBy: "user", // "user" or "opponent"
      ballColor: "red", // default
      points: 1, // derived from ballColor
    };
    const updated = {
      ...localGame,
      attempts: [...localGame.attempts, newAttempt],
    };
    recalcScoresAndUpdate(updated);
  };

  // Update an existing attempt
  const handleAttemptChange = (index, field, value) => {
    const updatedAttempts = localGame.attempts.map((att, i) => {
      if (i === index) {
        // If ballColor changed, recalc points from ballToPointsMap
        if (field === "ballColor") {
          return {
            ...att,
            ballColor: value,
            points: ballToPointsMap[value],
          };
        } else {
          return { ...att, [field]: value };
        }
      }
      return att;
    });
    const updated = { ...localGame, attempts: updatedAttempts };
    recalcScoresAndUpdate(updated);
  };

  // Remove an attempt
  const handleRemoveAttempt = (index) => {
    const updatedAttempts = localGame.attempts.filter((_, i) => i !== index);
    const updated = { ...localGame, attempts: updatedAttempts };
    recalcScoresAndUpdate(updated);
  };

  // Recalculate scores each time attempts or anything changes
  const recalcScoresAndUpdate = (updatedGame) => {
    let userScore = 0;
    let opponentScore = 0;
    updatedGame.attempts.forEach((attempt) => {
      const points = attempt.points || 0;
      if (attempt.shotBy === "user") {
        userScore += points;
      } else {
        opponentScore += points;
      }
    });
    updatedGame.userScore = userScore;
    updatedGame.opponentScore = opponentScore;

    // Simple winner logic
    if (userScore > opponentScore) {
      updatedGame.winner = "user";
    } else if (opponentScore > userScore) {
      updatedGame.winner = "opponent";
    } else {
      updatedGame.winner = null;
    }

    updateParent(updatedGame);
  };

  return (
    <div className={formClasses.gameCard}>
      <div className={formClasses.gameHeader}>
        <h3 className={formClasses.gameTitle}>Snooker Game #{gameNumber}</h3>
        <button onClick={onRemove} className={formClasses.removeButton}>
          Remove Game
        </button>
      </div>

      <div className={formClasses.formGroup}>
        <label className={formClasses.label}>Game Type:</label>
        <select
          value={localGame.gameType}
          onChange={handleGameTypeChange}
          className={formClasses.select}
        >
          <option value="15-red">15-Red</option>
          <option value="6-red">6-Red</option>
        </select>
      </div>

      {/* Scoring Guidance */}
      <div className={formClasses.guidanceBox}>
        <h4 className={formClasses.guidanceTitle}>📋 Snooker Scoring Guide</h4>
        <div className={formClasses.guidanceContent}>
          <p>
            <strong>Game Type:</strong>{" "}
            {localGame.gameType === "15-red"
              ? "15-Red Snooker"
              : "6-Red Snooker"}
          </p>
          <p>
            <strong>Ball Values:</strong> Red(1) → Yellow(2) → Green(3) →
            Brown(4) → Blue(5) → Pink(6) → Black(7)
          </p>
          <p>
            <strong>How to Play:</strong> Pot red balls first, then colored
            balls in order. Miss = 0 points.
          </p>
          <p>
            <strong>Winning:</strong> Player with the highest score wins when
            all balls are potted.
          </p>
        </div>
      </div>

      {/* Player Selection - Changed to one row like Golf */}
      <div className={formClasses.playerSelectionRow}>
        <div className={formClasses.formGroup}>
          <label className={formClasses.label}>Player A:</label>
          <PlayerSlot
            availableUsers={availableUsers}
            selectedPlayerId={localGame.playerA}
            selectedPlayerName={
              localGame.playerAName ||
              (localGame.playerA
                ? availableUsers.find(
                    (u) => (u.id || u.user_id) == localGame.playerA
                  )?.full_name || "Player A"
                : "Player A")
            }
            selectedUserIds={[localGame.playerA, localGame.playerB].filter(
              (id) => id && !String(id).startsWith("custom_")
            )}
            onSelectPlayer={(userInfo, customName) => {
              const playerId =
                userInfo?.id ||
                userInfo?.user_id ||
                (customName ? `custom_${Date.now()}` : null);
              const playerName =
                userInfo?.full_name || customName || "Guest Player";
              const updated = {
                ...localGame,
                playerA: playerId,
                playerAName: playerName,
              };
              updateParent(updated);
            }}
          />
        </div>

        <div className={formClasses.formGroup}>
          <label className={formClasses.label}>Player B:</label>
          <PlayerSlot
            availableUsers={availableUsers}
            selectedPlayerId={localGame.playerB}
            selectedPlayerName={
              localGame.playerBName ||
              (localGame.playerB
                ? availableUsers.find(
                    (u) => (u.id || u.user_id) == localGame.playerB
                  )?.full_name || "Player B"
                : "Player B")
            }
            selectedUserIds={[localGame.playerA, localGame.playerB].filter(
              (id) => id && !String(id).startsWith("custom_")
            )}
            onSelectPlayer={(userInfo, customName) => {
              const playerId =
                userInfo?.id ||
                userInfo?.user_id ||
                (customName ? `custom_${Date.now()}` : null);
              const playerName =
                userInfo?.full_name || customName || "Guest Player";
              const updated = {
                ...localGame,
                playerB: playerId,
                playerBName: playerName,
              };
              updateParent(updated);
            }}
          />
        </div>
      </div>

      <div>
        <button onClick={handleAddAttempt} className={formClasses.addButton}>
          Add Attempt
        </button>
      </div>

      {/* List all attempts */}
      {localGame.attempts.length > 0 && (
        <div className={formClasses.tableWrapper}>
          <table className={formClasses.table}>
            <thead>
              <tr>
                <th className={formClasses.tableHeader}>Shot By</th>
                <th className={formClasses.tableHeader}>Ball Color</th>
                <th className={formClasses.tableHeader}>Points</th>
                <th className={formClasses.tableHeader}>Action</th>
              </tr>
            </thead>
            <tbody>
              {localGame.attempts.map((attempt, i) => (
                <tr key={i}>
                  {/* Shot by user or opponent */}
                  <td className={formClasses.tableCell}>
                    <select
                      value={attempt.shotBy}
                      onChange={(e) =>
                        handleAttemptChange(i, "shotBy", e.target.value)
                      }
                      className="px-2 py-1 border border-gray-300 rounded w-full"
                    >
                      <option value="user">
                        {localGame.playerAName || "Player A"}
                      </option>
                      <option value="opponent">
                        {localGame.playerBName || "Player B"}
                      </option>
                    </select>
                  </td>

                  {/* Ball color */}
                  <td className={formClasses.tableCell}>
                    <div className={formClasses.ballColorCell}>
                      <span
                        className={formClasses.colorIndicator}
                        style={{
                          backgroundColor: ballColorMap[attempt.ballColor],
                        }}
                      ></span>
                      <select
                        value={attempt.ballColor}
                        onChange={(e) =>
                          handleAttemptChange(i, "ballColor", e.target.value)
                        }
                        className="px-2 py-1 border border-gray-300 rounded w-full"
                      >
                        <option value="miss">Miss (0pt)</option>
                        <option value="red">Red (1pt)</option>
                        <option value="yellow">Yellow (2pts)</option>
                        <option value="green">Green (3pts)</option>
                        <option value="brown">Brown (4pts)</option>
                        <option value="blue">Blue (5pts)</option>
                        <option value="pink">Pink (6pts)</option>
                        <option value="black">Black (7pts)</option>
                      </select>
                    </div>
                  </td>

                  {/* Points (read-only) */}
                  <td
                    className={`${formClasses.tableCell} font-semibold text-lg`}
                  >
                    {attempt.points}
                  </td>

                  {/* Remove attempt */}
                  <td className={formClasses.tableCell}>
                    <button
                      onClick={() => handleRemoveAttempt(i)}
                      className={formClasses.removeAttemptButton}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Display scores and winner */}
      <div className={formClasses.scoreCard}>
        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localGame.playerAName || "Player A"} Score
          </div>
          <div className={formClasses.scoreValue}>{localGame.userScore}</div>
        </div>

        <div className={formClasses.scoreBox}>
          <div className={formClasses.scoreLabel}>
            {localGame.playerBName || "Player B"} Score
          </div>
          <div className={formClasses.scoreValue}>
            {localGame.opponentScore}
          </div>
        </div>

        <div className={formClasses.winnerBox}>
          <div className={formClasses.winnerLabel}>Winner</div>
          <div className={formClasses.winnerValue}>
            {localGame.winner === null
              ? "Draw"
              : localGame.winner === "user"
              ? localGame.playerAName || "Player A"
              : localGame.playerBName || "Player B"}
          </div>
        </div>
      </div>
    </div>
  );
};

// PlayerSlot component for single player selection per slot
const PlayerSlot = ({
  availableUsers,
  selectedPlayerId,
  selectedPlayerName,
  onSelectPlayer,
  selectedUserIds = [],
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const isSlotEmpty =
    !selectedPlayerId ||
    !selectedPlayerName ||
    selectedPlayerName === "Player A" ||
    selectedPlayerName === "Player B";

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSave = () => {
    if (useCustomName && customName.trim()) {
      onSelectPlayer(null, customName.trim());
      setCustomName("");
      setIsEditing(false);
    } else if (!useCustomName && selectedUser) {
      onSelectPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setCustomName("");
    setUseCustomName(false);
    setIsEditing(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    selectedPlayer: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 12px",
      backgroundColor: "#f7fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      fontSize: "14px",
      color: "#2c3e50",
    },
    editButton: {
      backgroundColor: "#f39c12",
      color: "white",
      border: "none",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      cursor: "pointer",
    },
    select: {
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #cbd5e0",
      fontSize: "14px",
      width: "200px",
    },
    input: {
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #cbd5e0",
      fontSize: "14px",
      width: "200px",
    },
    button: {
      backgroundColor: "#3498db",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      cursor: "pointer",
    },
    cancelButton: {
      backgroundColor: "#95a5a6",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      cursor: "pointer",
    },
    toggle: {
      fontSize: "12px",
      color: "#3498db",
      cursor: "pointer",
      textDecoration: "underline",
    },
    emptySlot: {
      padding: "12px",
      border: "2px dashed #cbd5e0",
      borderRadius: "6px",
      textAlign: "center",
      color: "#718096",
      fontSize: "14px",
      cursor: "pointer",
      backgroundColor: "#f8fafc",
    },
  };

  // Tailwind styles
  const slotClasses = {
    container: "flex flex-col gap-2",
    selectedPlayer:
      "flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800",
    editButton:
      "bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded text-xs transition-colors",
    input: "px-2 py-2 border border-gray-300 rounded text-sm w-full",
    suggestionsDropdown:
      "absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b max-h-48 overflow-y-auto z-50 shadow-lg",
    suggestionItem:
      "px-3 py-2 cursor-pointer border-b border-gray-100 text-sm transition-colors hover:bg-gray-50",
    suggestionItemDisabled: "opacity-50 cursor-not-allowed bg-gray-50",
    button:
      "bg-[#3498db] hover:bg-[#2980b9] text-white px-3 py-1 rounded text-xs transition-colors disabled:opacity-50",
    cancelButton:
      "bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-xs transition-colors",
    toggle:
      "text-xs text-blue-500 cursor-pointer underline hover:text-blue-600",
    emptySlot:
      "px-3 py-3 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500 text-sm cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors",
  };

  // Show editing UI if editing or no player selected
  if (isEditing || isSlotEmpty) {
    return (
      <div className={slotClasses.container}>
        <div className="flex flex-col gap-2">
          {!useCustomName ? (
            <>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => {
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  placeholder="Search for system users..."
                  className={slotClasses.input}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className={slotClasses.suggestionsDropdown}>
                    {suggestions.map((user) => {
                      const userId = String(user.id || user.user_id);
                      const currentPlayerId = String(selectedPlayerId || "");
                      const isAlreadySelected =
                        selectedUserIds
                          .map((id) => String(id))
                          .includes(userId) && userId !== currentPlayerId;
                      return (
                        <div
                          key={user.id || user.user_id}
                          style={{
                            padding: "8px 12px",
                            cursor: isAlreadySelected
                              ? "not-allowed"
                              : "pointer",
                            borderBottom: "1px solid #f0f0f0",
                            fontSize: "14px",
                            opacity: isAlreadySelected ? 0.5 : 1,
                            backgroundColor: isAlreadySelected
                              ? "#f0f0f0"
                              : "white",
                          }}
                          onClick={() =>
                            !isAlreadySelected && handleSelectUser(user)
                          }
                          onMouseEnter={(e) => {
                            if (!isAlreadySelected) {
                              e.currentTarget.style.backgroundColor = "#f7fafc";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isAlreadySelected) {
                              e.currentTarget.style.backgroundColor = "white";
                            }
                          }}
                        >
                          <div style={{ fontWeight: "500" }}>
                            {user.full_name || user.username}
                            {isAlreadySelected && (
                              <span
                                style={{ color: "#e53e3e", marginLeft: "8px" }}
                              >
                                (Already Added)
                              </span>
                            )}
                          </div>
                          {user.email && (
                            <div style={{ fontSize: "12px", color: "#718096" }}>
                              {user.email}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {showSuggestions &&
                  searchTerm.length >= 2 &&
                  suggestions.length === 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        backgroundColor: "white",
                        border: "1px solid #cbd5e0",
                        borderTop: "none",
                        borderRadius: "0 0 4px 4px",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 12px",
                          fontSize: "14px",
                          color: "#718096",
                          textAlign: "center",
                        }}
                      >
                        No users found
                      </div>
                    </div>
                  )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={!selectedUser}
                  className={slotClasses.button}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className={slotClasses.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Enter custom player name"
                className={slotClasses.input}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={!customName.trim()}
                  className={slotClasses.button}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className={slotClasses.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
          <span
            onClick={() => setUseCustomName(!useCustomName)}
            className={slotClasses.toggle}
          >
            {useCustomName ? "Use system users" : "Add custom name"}
          </span>
        </div>
      </div>
    );
  }

  // If player is selected and not editing, show the selected player
  return (
    <div className={slotClasses.container}>
      <div className={slotClasses.selectedPlayer}>
        <span>{selectedPlayerName}</span>
        <button
          onClick={() => setIsEditing(true)}
          className={slotClasses.editButton}
        >
          Edit
        </button>
      </div>
    </div>
  );
};
